import { Box, Button, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import {
  ArrowsOut,
  Plus,
  ShareNetwork,
  UploadSimple,
  X,
} from '@phosphor-icons/react';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  NodeResizer,
  Position,
  useEdges,
  useNodes,
  useReactFlow,
} from 'reactflow';
import { UploadService } from '../../service/UploadService';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { arrayDataToText } from '../../util/dataToText';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import InputHandle from '../UI/InputHandle/InputHandle';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import { CollapseButton } from './MediaNode/components/CollapseButton';
import { MediaModal } from './MediaNode/components/MediaModal';
import { MediaPreview } from './MediaNode/components/MediaPreview';
import { getMediaType, isUrl, isValidMediaFile, normalizeUrls } from './MediaNode/utils/mediaUtils';

const DEBOUNCE_DELAY = 400;

const COLLAPSED_WIDTH = 200;
const COLLAPSED_HEIGHT = 60;
const EXPANDED_DEFAULT_WIDTH = 450;
const EXPANDED_MIN_WIDTH = 300;
const EXPANDED_MIN_HEIGHT = 200;
const EXPANDED_MIN_HEIGHT_PX = '150px';
const COLLAPSED_MIN_HEIGHT_PX = '60px';

interface UrlWithId {
  url: string;
  id: string;
  edgeId?: string;
  isEdge?: boolean;
  isUploading?: boolean;
  fileName?: string;
}

interface SourceNodeData {
  text?: string;
  textData?: string;
}

const hasTextData = (data: unknown): data is SourceNodeData => {
  return (
    typeof data === 'object' &&
    data !== null &&
    ('text' in data || 'textData' in data)
  );
};

const createUrlsComparisonString = (urls: UrlWithId[]): string => {
  return urls
    .map(u => `${u.url}|||${u.id}`)
    .sort()
    .join(',');
};

const formatUrlsAsText = (urls: UrlWithId[]): string => {
  const parts = urls
    .map(item => (item?.url && typeof item.url === 'string' ? item.url.trim() : ''))
    .filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return parts.join(', ');
};

const parseSavedUrlsNonEdge = (savedUrls: unknown): UrlWithId[] => {
  if (!Array.isArray(savedUrls)) return [];
  return savedUrls
    .filter((item: { url?: string }) => item && typeof item === 'object' && item.url)
    .filter((item: { isEdge?: boolean }) => !item.isEdge)
    .map((item: { url: string; id?: string; isUploading?: boolean; fileName?: string }) => ({
      url: item.url,
      id: item.id || crypto.randomUUID(),
      isUploading: item.isUploading,
      fileName: item.fileName,
    }));
};

const mergeNonEdgeWithEdgeUrls = (
  nonEdge: UrlWithId[],
  edge: UrlWithId[],
): UrlWithId[] => [...nonEdge, ...edge];

const toPersistedNodeUrls = (merged: UrlWithId[]): UrlWithId[] =>
  merged.filter(u => !u.isEdge);

const buildPersistPayload = (merged: UrlWithId[]) => ({
  urls: toPersistedNodeUrls(merged),
  text: formatUrlsAsText(merged),
});

const updateNodeSize = (
  nodes: any[],
  nodeId: string,
  width: number,
  height: number | undefined,
  setNodeState: (updater: (nodes: any[]) => any[]) => void,
) => {
  setNodeState(nds =>
    nds.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          width,
          height,
          style: {
            ...node.style,
            width,
            height,
          },
        };
      }
      return node;
    }),
  );
};

function MediaBox({ id, data, type, selected, width, height }: NodeProps) {
  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType?.colors;
  const { setNodes: setNodeState, setEdges, getNodes, getZoom } = useReactFlow();
  const { setSaving, setSchema, onEdgeDelete } = useFlowsStore(state => state);
  const edges = useEdges();
  const nodes = useNodes();
  const { flowId } = useParams();

  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState<'url' | 'preview'>(
    data?.activeTab || 'preview',
  );
  const [modalUrl, setModalUrl] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [urls, setUrls] = useState<UrlWithId[]>([]);
  const [, setUploadingFiles] = useState<Map<string, string>>(new Map());
  const [currentSize, setCurrentSize] = useState<{
    width: number;
    height: number | undefined;
  } | null>(null);

  const isCollapsed = !!data?.collapsed;
  const defaultWidth = isCollapsed ? COLLAPSED_WIDTH : EXPANDED_DEFAULT_WIDTH;
  const defaultHeight = isCollapsed ? COLLAPSED_HEIGHT : undefined;

  const nodeWidth =
    currentSize?.width ??
    (width as number) ??
    (isCollapsed ? COLLAPSED_WIDTH : data?.size?.width) ??
    defaultWidth;
  const nodeHeight =
    currentSize?.height ??
    (height as number) ??
    (isCollapsed ? COLLAPSED_HEIGHT : data?.size?.height) ??
    defaultHeight;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const edgeUrlsRef = useRef<UrlWithId[]>([]);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const externalFilesProcessedRef = useRef(false);

  const connectedEdges = useMemo(() => {
    return edges.filter(
      edge =>
        edge.target === id &&
        edge.targetHandle === 'mediaInput' &&
        edge.source &&
        edge.sourceHandle &&
        edge.id,
    );
  }, [edges, id]);

  const hasConnectedData = connectedEdges.length > 0;

  const saveData = useCallback(
    (payload: any, changeType: string = 'default') => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        objectData: payload,
        setSchema,
        changeType,
      });
    },
    [edges, flowId, id, setNodeState, setSaving, setSchema],
  );

  const handleResize = useCallback(
    (event: any, params?: { width?: number; height?: number }) => {
      const nodes = getNodes();
      const currentNode = nodes.find(node => node.id === id);
      if (!currentNode) return;

      const newWidth =
        params?.width ??
        (currentNode.width as number) ??
        EXPANDED_DEFAULT_WIDTH;
      const newHeight =
        params?.height ?? (currentNode.height as number) ?? undefined;

      setCurrentSize({ width: newWidth, height: newHeight });
      updateNodeSize(getNodes(), id, newWidth, newHeight, setNodeState);
    },
    [id, getNodes, setNodeState, saveData],
  );

  const handleResizeEnd = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);

    if (currentNode && currentNode.width && currentNode.height) {
      const finalWidth = currentNode.width as number;
      const finalHeight = currentNode.height as number;

      saveData({ size: { width: finalWidth, height: finalHeight } });
    }

    setCurrentSize(null);
  }, [id, getNodes, saveData]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const nodeElement = document.querySelector(
        `[data-id="${id}"]`,
      ) as HTMLElement;
      if (!nodeElement) return;

      const nodeRect = nodeElement.getBoundingClientRect();

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = nodeWidth;
      const startHeight = nodeHeight || nodeRect.height;

      let animationFrameId: number | null = null;
      let pendingUpdate: { width: number; height: number } | null = null;

      const updateSize = () => {
        if (pendingUpdate) {
          handleResize(null, pendingUpdate);
          pendingUpdate = null;
        }
        animationFrameId = null;
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const currentZoom = getZoom();
        const deltaX = (moveEvent.clientX - startX) / currentZoom;
        const deltaY = (moveEvent.clientY - startY) / currentZoom;

        const newWidth = Math.max(EXPANDED_MIN_WIDTH, startWidth + deltaX);
        const newHeight = Math.max(EXPANDED_MIN_HEIGHT, startHeight + deltaY);

        pendingUpdate = { width: newWidth, height: newHeight };

        if (!animationFrameId) {
          animationFrameId = requestAnimationFrame(updateSize);
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }

        if (pendingUpdate) {
          handleResize(null, pendingUpdate);
        }

        handleResizeEnd();
      };

      document.addEventListener('mousemove', handleMouseMove, {
        passive: true,
      });
      document.addEventListener('mouseup', handleMouseUp);
    },
    [id, nodeWidth, nodeHeight, handleResize, handleResizeEnd, getZoom],
  );

  const edgeUrls = useMemo(() => {
    if (!hasConnectedData) return [];

    const urlsFromEdges: UrlWithId[] = [];

    connectedEdges.forEach(edge => {
      const sourceNode = nodes.find(node => node.id === edge.source);
      if (!sourceNode) return;

      const text = hasTextData(sourceNode.data)
        ? arrayDataToText(sourceNode.data.textData) || sourceNode.data.text || ''
        : '';

      if (!text || typeof text !== 'string' || !text.trim()) return;

      const extractedUrls = normalizeUrls(text);
      extractedUrls.forEach((url, urlIndex) => {
        urlsFromEdges.push({
          url,
          id: `${edge.id}__${urlIndex}`,
          edgeId: edge.id,
          isEdge: true,
        });
      });
    });

    return urlsFromEdges;
  }, [hasConnectedData, connectedEdges, nodes]);

  edgeUrlsRef.current = edgeUrls;

  useEffect(() => {
    setUrls(prev => {
      const nonEdgeFromState = prev.filter(u => !u.isEdge);
      const fromData = parseSavedUrlsNonEdge(data?.urls);
      let nonEdge = nonEdgeFromState;
      if (nonEdge.length === 0 && prev.length === 0) {
        nonEdge = fromData;
      }
      const merged = mergeNonEdgeWithEdgeUrls(nonEdge, edgeUrls);
      if (createUrlsComparisonString(prev) === createUrlsComparisonString(merged)) {
        return prev;
      }
      return merged;
    });
  }, [edgeUrls, data?.urls]);

  const saveUrlsDebounced = useCallback(
    (urlsToSave: UrlWithId[]) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        saveData(buildPersistPayload(urlsToSave));
      }, DEBOUNCE_DELAY);
    },
    [saveData],
  );

  const setNotification = useNotificationStore((state: any) => state.setNotification);

  const uploadFilesWithTracking = useCallback(
    async (files: File[]) => {
      if (!flowId || files.length === 0) return [];

      const uploadingItems: UrlWithId[] = files.map(file => {
        const fileId = crypto.randomUUID();
        setUploadingFiles(prev => new Map(prev).set(fileId, file.name));
        return {
          url: file.name,
          id: fileId,
          isUploading: true,
          fileName: file.name,
        };
      });

      setUrls(prev => {
        const nonEdge = prev.filter(u => !u.isEdge);
        return mergeNonEdgeWithEdgeUrls([...nonEdge, ...uploadingItems], edgeUrlsRef.current);
      });
      const fileIds = uploadingItems.map(item => item.id);

      try {
        setNotification({
          type: 'info',
          message: 'Uploading files...',
        });

        const uploadPromises = files.map(async file => {
          const formData = new FormData();
          formData.append('file', file);
          const response = await UploadService.uploadFile(formData, flowId);
          return response.data?.src || '';
        });

        const uploadedUrls = await Promise.all(uploadPromises);
        const validUrls = uploadedUrls.filter(Boolean);

        if (validUrls.length > 0) {
          setNotification({
            type: 'success',
            message: 'Files uploaded successfully',
          });

          setUrls(prev => {
            const nonEdge = prev.filter(u => !u.isEdge);
            const filtered = nonEdge.filter(u => !fileIds.includes(u.id));
            const urlsWithIds = validUrls.map((url: string, index: number) => ({
              url,
              id: fileIds[index] || crypto.randomUUID(),
              isUploading: false,
            }));
            const updatedNonEdge = [...filtered, ...urlsWithIds];
            const updatedUrls = mergeNonEdgeWithEdgeUrls(
              updatedNonEdge,
              edgeUrlsRef.current,
            );
            saveUrlsDebounced(updatedUrls);
            return updatedUrls;
          });
        }

        setUploadingFiles(prev => {
          const next = new Map(prev);
          fileIds.forEach(id => next.delete(id));
          return next;
        });

        return validUrls;
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: error.message || 'Error uploading files',
        });

        setUrls(prev => {
          const nonEdge = prev
            .filter(u => !u.isEdge)
            .filter(u => !fileIds.includes(u.id));
          return mergeNonEdgeWithEdgeUrls(nonEdge, edgeUrlsRef.current);
        });
        setUploadingFiles(prev => {
          const next = new Map(prev);
          fileIds.forEach(id => next.delete(id));
          return next;
        });
        return [];
      }
    },
    [flowId, setNotification, saveUrlsDebounced],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return;

      const files = Array.from(e.target.files);
      const validFiles = files.filter(isValidMediaFile);

      if (validFiles.length === 0) return;

      await uploadFilesWithTracking(validFiles);
    },
    [uploadFilesWithTracking],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      const text = e.dataTransfer.getData('text');
      const textUrls = text ? normalizeUrls(text) : [];

      if (files.length > 0) {
        const validFiles = files.filter(isValidMediaFile);
        if (validFiles.length > 0) {
          await uploadFilesWithTracking(validFiles);
        }
      }

      if (textUrls.length > 0) {
        setUrls(prev => {
          const nonEdge = prev.filter(u => !u.isEdge);
          const urlsWithIds = textUrls.map(url => ({
            url,
            id: crypto.randomUUID(),
          }));
          const updatedUrls = mergeNonEdgeWithEdgeUrls(
            [...nonEdge, ...urlsWithIds],
            edgeUrlsRef.current,
          );
          saveUrlsDebounced(updatedUrls);
          return updatedUrls;
        });
      }
    },
    [uploadFilesWithTracking, saveUrlsDebounced],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  useEffect(() => {
    if (externalFilesProcessedRef.current) return;

    const externalFiles = data?.externalFiles;
    if (!externalFiles || !Array.isArray(externalFiles) || externalFiles.length === 0) {
      return;
    }

    const validFiles = externalFiles.filter((file): file is File => file instanceof File);

    externalFilesProcessedRef.current = true;

    if (validFiles.length > 0) {
      uploadFilesWithTracking(validFiles);
    }
  }, [data?.externalFiles, uploadFilesWithTracking]);

  const handleToggleCollapsed = useCallback(() => {
    saveData({
      collapsed: !data?.collapsed,
    });
  }, [data?.collapsed, saveData]);

  const handleRemoveUrl = useCallback(
    (urlId: string) => {
      const urlObj = urls.find(u => u.id === urlId);
      if (urlObj?.isEdge) {
        const flowEdgeId = urlObj.edgeId ?? urlObj.id;

        const edgesToDelete = edges.filter(
          edge =>
            edge.id === flowEdgeId &&
            edge.target === id &&
            edge.targetHandle === 'mediaInput',
        );

        if (edgesToDelete.length > 0) {
          const edgesToRemoveIds = edgesToDelete.map(e => e.id);
          setEdges(eds =>
            eds.filter(edge => !edgesToRemoveIds.includes(edge.id)),
          );
          onEdgeDelete(edgesToDelete, edgesToDelete[0]?.id ?? '');
        }
      } else {
        const newUrls = urls.filter(u => u.id !== urlId);
        setUrls(newUrls);

        saveData(buildPersistPayload(newUrls));
      }
    },
    [urls, edges, saveData, id, setEdges, onEdgeDelete],
  );

  const handleAddUrl = useCallback(() => {
    if (!isUrl(urlInput)) return;

    const newUrl: UrlWithId = {
      url: urlInput,
      id: crypto.randomUUID(),
    };

    setUrls(prev => {
      const nonEdge = prev.filter(u => !u.isEdge);
      const updatedUrls = mergeNonEdgeWithEdgeUrls(
        [...nonEdge, newUrl],
        edgeUrlsRef.current,
      );
      saveUrlsDebounced(updatedUrls);
      return updatedUrls;
    });
    setUrlInput('');
  }, [urlInput, saveUrlsDebounced]);

  const checkAndSaveUrls = useCallback(
    (newUrls: UrlWithId[], condition: boolean = true) => {
      if (!condition) return;

      const currentUrls = (data?.urls || []) as UrlWithId[];
      const currentUrlsStr = createUrlsComparisonString(currentUrls);
      const newPersisted = toPersistedNodeUrls(newUrls);
      const newUrlsStr = createUrlsComparisonString(newPersisted);
      const newText = formatUrlsAsText(newUrls);

      if (currentUrlsStr !== newUrlsStr || data?.text !== newText) {
        saveData(buildPersistPayload(newUrls));
      }
    },
    [data?.urls, data?.text, saveData],
  );

  useEffect(() => {
    if (hasConnectedData) return;
    if (urls.length > 0) {
      checkAndSaveUrls(urls);
    }
  }, [urls, hasConnectedData, checkAndSaveUrls]);

  useEffect(() => {
    if (!hasConnectedData) {
      const urlsText = formatUrlsAsText(urls);
      if (data?.text !== urlsText) {
        saveData(buildPersistPayload(urls));
      }
    }
  }, [urls, hasConnectedData, data?.text, saveData]);

  useEffect(() => {
    if (!hasConnectedData) return;
    checkAndSaveUrls(urls);
  }, [hasConnectedData, edgeUrls, urls, checkAndSaveUrls]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (data?.activeTab && data.activeTab !== activeTab) {
      setActiveTab(data.activeTab);
    }
  }, [data?.activeTab]);

  useEffect(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    const isCollapsed = !!data?.collapsed;

    if (isCollapsed) {
      if (
        currentNode &&
        (currentNode.width !== COLLAPSED_WIDTH ||
          currentNode.height !== COLLAPSED_HEIGHT)
      ) {
        updateNodeSize(
          getNodes(),
          id,
          COLLAPSED_WIDTH,
          COLLAPSED_HEIGHT,
          setNodeState,
        );
      }
    } else {
      if (currentNode && (!currentNode.width || !currentNode.height)) {
        const savedWidth = data?.size?.width || EXPANDED_DEFAULT_WIDTH;
        const savedHeight = data?.size?.height || undefined;
        updateNodeSize(getNodes(), id, savedWidth, savedHeight, setNodeState);
      } else if (data?.size?.width && data?.size?.height) {
        const savedWidth = data.size.width;
        const savedHeight = data.size.height;

        if (
          currentNode &&
          (currentNode.width !== savedWidth ||
            currentNode.height !== savedHeight)
        ) {
          updateNodeSize(getNodes(), id, savedWidth, savedHeight, setNodeState);
        }
      }
    }
  }, [data?.size, data?.collapsed, id, setNodeState, getNodes]);

  const tabs = ['url', 'preview'] as const;

  return (
    <>
      <Node
        id={id}
        data={data}
        isLoading={false}
        type={type}
        showTokensUsed={false}
        edges={edges}
      >
        <CollapseButton
          isCollapsed={!!data?.collapsed}
          onToggle={handleToggleCollapsed}
        />

        {selected && !isCollapsed && (
          <NodeResizer
            minHeight={EXPANDED_MIN_HEIGHT}
            minWidth={EXPANDED_MIN_WIDTH}
            onResize={handleResize}
            onResizeEnd={handleResizeEnd}
            isVisible={false}
          />
        )}

        <Box
          sx={{
            position: 'relative',
            overflow: 'visible',
            width: `${nodeWidth - 1}px`,
            height: nodeHeight ? `${nodeHeight}px` : 'auto',
            minHeight: isCollapsed
              ? COLLAPSED_MIN_HEIGHT_PX
              : EXPANDED_MIN_HEIGHT_PX,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(16,24,40,0.08)',
              width: '99.6%', // 0.4% padding to avoid overflow
              height: nodeHeight ? `${nodeHeight}px` : 'auto',
              minHeight: isCollapsed
                ? COLLAPSED_MIN_HEIGHT_PX
                : EXPANDED_MIN_HEIGHT_PX,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {selected && !isCollapsed && (
              <Box
                onMouseDown={handleResizeStart}
                className="nodrag"
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: findColor?.color || '#3650DE',
                  borderRadius: '4px',
                  cursor: 'nwse-resize',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: findColor?.color || '#3650DE',
                    opacity: 0.9,
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <ArrowsOut
                  size={14}
                  color="#fff"
                  weight="bold"
                  style={{ transform: 'rotate(45deg)' }}
                />
              </Box>
            )}

            {!data?.collapsed && (
              <Box
                p={2.5}
                pb={1}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  overflow: 'hidden',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {tabs.map(tab => (
                  <Box
                    key={tab}
                    component="button"
                    onClick={() => {
                      setActiveTab(tab);
                      saveData({ activeTab: tab });
                    }}
                    style={{
                      border: 'none',
                      background: activeTab === tab ? '#e8ebfc' : '#F2F4F7',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      borderRadius: '8px',
                      color: activeTab === tab ? '#3650DE' : '#667085',
                    }}
                  >
                    {tab === 'url' ? 'URL' : 'Preview'}
                  </Box>
                ))}
              </Box>
            )}

            <Box
              p={2.5}
              pt={1}
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{
                position: 'relative',
                overflow: nodeHeight ? 'auto' : 'visible',
                flex: 1,
                minHeight: 0,
              }}
            >
              {!data?.collapsed && (
                <>
                  {activeTab === 'url' ? (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*,audio/*,.pdf"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                      />
                      {urls.length === 0 ? (
                        <Box
                          className="nodrag"
                          sx={{
                            border: `2px dashed ${isDragging ? '#667085' : '#D0D5DD'
                              }`,
                            borderRadius: '8px',
                            padding: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onClick={handleClick}
                          onDragOver={e => {
                            e.preventDefault();
                            setIsDragging(true);
                          }}
                          onDragLeave={() => setIsDragging(false)}
                          onDrop={handleDrop}
                          tabIndex={0}
                        >
                          <UploadSimple
                            size={32}
                            weight="regular"
                            color={isDragging ? '#667085' : '#98A2B3'}
                          />
                          <Typography
                            sx={{
                              fontSize: '14px',
                              color: isDragging ? '#667085' : '#98A2B3',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            Connect or upload visual displays.
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              color: isDragging ? '#667085' : '#98A2B3',
                              textAlign: 'center',
                            }}
                          >
                            PDFs, video URLs, videos, or images.
                          </Typography>
                        </Box>
                      ) : (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          {urls.map(urlObj => (
                            <Box
                              key={urlObj.id}
                              className="nodrag"
                              sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px',
                                backgroundColor: '#fff',
                                border: '1px solid #667085',
                                borderRadius: '4px',
                                '&:hover .remove-button': {
                                  opacity: urlObj.isUploading ? 0 : 1,
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  width: '100%',
                                  minWidth: 0,
                                  pr: '4px',
                                }}
                              >
                                {urlObj.isEdge && (
                                  <ShareNetwork
                                    size={18}
                                    color="#667085"
                                    weight="regular"
                                    style={{ flexShrink: 0 }}
                                  />
                                )}
                                <Typography
                                  sx={{
                                    flex: 1,
                                    fontSize: '14px',
                                    color: '#101828',
                                    wordBreak: 'break-all',
                                    minWidth: 0,
                                    userSelect: 'text',
                                    cursor: 'text',
                                  }}
                                >
                                  {urlObj.url}
                                </Typography>
                              </Box>
                              {!urlObj.isUploading && (
                                <IconButton
                                  className="remove-button"
                                  onClick={() => handleRemoveUrl(urlObj.id)}
                                  sx={{
                                    position: 'absolute',
                                    right: 4,
                                    zIndex: 2,
                                    height: 'calc(100% - 2px)',
                                    borderRadius: '0px',
                                    opacity: 0,
                                    transition: 'opacity 0.1s ease',
                                    padding: '4px',
                                    backgroundColor: '#fff',
                                    '&:hover': {
                                      backgroundColor: '#fff',
                                    },
                                  }}
                                  title="Remove URL"
                                >
                                  <X size={18} color="#667085" />
                                </IconButton>
                              )}
                              {urlObj.isUploading && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 8,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: '12px',
                                      color: '#3650DE',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Uploading
                                  </Typography>
                                  <CircularProgress size={20} sx={{ color: '#3650DE' }} />
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Box
                        display="flex"
                        gap={1}
                        alignItems="start"
                        className="nodrag"
                      >
                        <TextField
                          label="Add URL"
                          value={urlInput}
                          onChange={e => setUrlInput(e.currentTarget.value)}
                          onFocus={() => setIsFocused(true)}
                          onBlur={() => setIsFocused(false)}
                          error={!!urlInput && !isUrl(urlInput)}
                          helperText={
                            !!urlInput && !isUrl(urlInput)
                              ? 'Enter a valid URL'
                              : ''
                          }
                          InputLabelProps={{
                            shrink: urlInput.length > 0 || isFocused,
                          }}
                          fullWidth
                        />
                        <Button
                          variant="contained"
                          onClick={handleAddUrl}
                          sx={{
                            background:
                              'linear-gradient(90deg, #BA2F2D 0%, #CA52F1 100%)',
                            color: '#fff',
                            minWidth: '48px',
                            '&.Mui-disabled': {
                              filter: 'grayscale(100%)',
                              opacity: 0.4,
                              color: '#fff',
                            },
                          }}
                          disabled={!isUrl(urlInput)}
                        >
                          <Plus size={18} weight="bold" />
                        </Button>
                        {urls.length > 0 && (
                          <Button
                            variant="contained"
                            onClick={handleClick}
                            sx={{
                              background:
                                'linear-gradient(90deg, #BA2F2D 0%, #CA52F1 100%)',
                              color: '#fff',
                              minWidth: '48px',
                            }}
                            title="Upload files"
                          >
                            <UploadSimple size={18} weight="bold" />
                          </Button>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      {urls.length === 0 ? (
                        <Typography sx={{ fontSize: '13px', color: '#667085' }}>
                          {hasConnectedData
                            ? 'No valid URLs found in connected nodes.'
                            : 'No media to display. Add URLs in the URL tab.'}
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            border: '1px solid #EAECF0',
                            borderRadius: '12px',
                            padding: 1.5,
                            background: '#fff',
                            boxShadow: '0 1px 3px rgba(16,24,40,0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          {urls.map(urlObj => {
                            const mediaType = getMediaType(urlObj.url);
                            return (
                              <MediaPreview
                                key={urlObj.id}
                                url={urlObj.url}
                                onClick={
                                  mediaType === 'image' || mediaType === 'pdf'
                                    ? () => setModalUrl(urlObj.url)
                                    : undefined
                                }
                              />
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>

          <InputHandle
            activeColor={findColor?.color}
            handleId="mediaInput"
            isActive={isActiveEdge(edges, id, 'mediaInput', 'target')}
            position={Position.Left}
            type="target"
            top="32px"
            left={-9}
          />

          <InputHandle
            activeColor={findColor?.color}
            handleId="mediaOutput"
            isActive={isActiveEdge(edges, id, 'mediaOutput', 'source')}
            position={Position.Right}
            type="source"
            top="32px"
            right={-8}
          />

          {data?.collapsed && (
            <Typography
              sx={{
                position: 'absolute',
                bottom: '8px',
                left: '12px',
                fontSize: '11px',
                color: '#98A2B3',
                fontWeight: 400,
                pointerEvents: 'none',
              }}
            >
              {urls.length === 0
                ? 'No Media'
                : `${urls.length} ${urls.length === 1 ? 'Media' : 'Medias'}`}
            </Typography>
          )}
        </Box>
      </Node>

      {modalUrl && (
        <MediaModal
          show={!!modalUrl}
          onHide={() => setModalUrl(null)}
          url={modalUrl}
        />
      )}
    </>
  );
}

export default memo(MediaBox);
